import { AlertTriangle, ShieldCheck, ShieldAlert, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

/**
 * Claim Safety warning dialog.
 *
 * Shown when a user prompt OR generated chapter contains clinical-decision /
 * diagnostic / treatment language. Philosophy: WARN, don't BLOCK.
 *
 * Props:
 *   - open           — boolean
 *   - result         — { classification, score, flagged_phrases[], reason, recommendation }
 *   - onCancel       — user wants to revise (close dialog)
 *   - onContinue     — user wants to continue anyway
 *   - mode           — 'pre-generation' (warn before LLM call) | 'post-generation' (warn after)
 */
export default function ClaimSafetyDialog({ open, result, onCancel, onContinue, mode = 'pre-generation' }) {
  if (!result) return null;

  const tone = {
    safe:      { Icon: ShieldCheck, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-900/40', label: 'Safe' },
    caution:   { Icon: ShieldAlert, color: 'text-amber-600',   bg: 'bg-amber-50 dark:bg-amber-950/30',     border: 'border-amber-200 dark:border-amber-900/40',     label: 'Caution' },
    high_risk: { Icon: AlertTriangle, color: 'text-rose-600',  bg: 'bg-rose-50 dark:bg-rose-950/30',       border: 'border-rose-200 dark:border-rose-900/40',       label: 'High risk' },
  }[result.classification] || {
    Icon: ShieldAlert, color: 'text-amber-600',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-900/40',
    label: 'Caution',
  };

  const isSafe = result.classification === 'safe';
  const Icon = tone.Icon;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel && onCancel()}>
      <DialogContent className="max-w-xl" data-testid="claim-safety-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            <Icon className={`w-5 h-5 ${tone.color}`} />
            Claim safety check — {tone.label}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {mode === 'pre-generation'
              ? 'We reviewed your prompt for clinical-decision language before generating.'
              : 'We reviewed the generated chapter for clinical-decision language.'}
          </DialogDescription>
        </DialogHeader>

        <div className={`rounded-lg border ${tone.border} ${tone.bg} p-4 mt-2`} data-testid="safety-result-card">
          <div className="flex items-baseline justify-between mb-2">
            <span className={`text-[10px] uppercase tracking-widest font-semibold ${tone.color}`}>
              {tone.label}
            </span>
            <span className="font-mono text-xs text-slate-500">
              risk score · <span className="font-semibold text-foreground">{result.score}/100</span>
              <span className="ml-2 text-[10px] text-slate-400">{result.engine}</span>
            </span>
          </div>
          <p className="text-sm text-foreground leading-relaxed">{result.reason}</p>
        </div>

        {Array.isArray(result.flagged_phrases) && result.flagged_phrases.length > 0 && (
          <div className="mt-4" data-testid="safety-flagged-list">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
              Flagged phrases
            </div>
            <ul className="space-y-1.5 text-sm">
              {result.flagged_phrases.slice(0, 8).map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <X className="w-3.5 h-3.5 mt-0.5 text-rose-500 flex-shrink-0" />
                  <span className="text-muted-foreground italic">"{p}"</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result.recommendation && !isSafe && (
          <div className="mt-4 border-l-2 border-[#1d4ed8] pl-3 py-1" data-testid="safety-recommendation">
            <div className="text-[10px] uppercase tracking-widest text-[#1d4ed8] dark:text-[#7aa9ff] font-semibold mb-1">
              How to soften
            </div>
            <p className="text-sm text-foreground">{result.recommendation}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={onCancel} data-testid="safety-cancel-btn">
            {isSafe ? 'Close' : 'Revise wording'}
          </Button>
          {!isSafe && onContinue && (
            <Button
              onClick={onContinue}
              className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white"
              data-testid="safety-continue-btn"
            >
              Continue anyway <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {isSafe && onContinue && (
            <Button onClick={onContinue} className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white" data-testid="safety-continue-btn">
              Continue <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
          InkPhD is positioned for research and educational use only. The platform
          does not block content, but flags clinical-decision phrasing so authors
          can keep their writing academic.
        </p>
      </DialogContent>
    </Dialog>
  );
}
